import { i18n } from '../services/i18n.js';
import { dbSim } from '../services/dbSim.js';
import { realtimeSim } from '../services/realtimeSim.js';
import { escapeHTML } from '../services/sanitize.js';
import { icon, reactionButtons } from '../services/icons.js';
import { playerSession } from '../services/playerSession.js';

export const PlayView = async (container) => {
  // Load player profile
  const player = playerSession.get();
  if (!player) {
    window.location.hash = '#/join';
    return;
  }

  container.innerHTML = `<div class="container" style="padding:40px; text-align:center; color:var(--color-text-secondary);">${i18n.t('play_loading')}</div>`;

  let session;
  let game;
  try {
    await dbSim.resumePlayer(player.sessionId);
    session = await dbSim.getSession(player.sessionId);
    game = await dbSim.getSessionGame(session.id);
    if (!session || !game) throw new Error('Session unavailable');
  } catch {
    playerSession.clear();
    window.location.hash = `#/join?pin=${encodeURIComponent(player.pin || '')}`;
    return;
  }

  let playState = "waiting_in_lobby"; // waiting_in_lobby, answering, submitted, question_ended, ended
  let questionStartTime = null;
  let submissionResult = null; // { isCorrect, pointsAwarded, score, streak }
  let answerSubmitting = false;

  const render = async () => {
    // Refresh session data
    session = await dbSim.getSession(player.sessionId);
    game = await dbSim.getSessionGame(session.id);
    
    if (session.status === "lobby") {
      playState = "waiting_in_lobby";
    } else if (session.status === "active") {
      // Check if already submitted for current question
      const responses = await dbSim.getResponses(session.id);
      const question = game.questions[session.currentQuestionIndex];
      const alreadyAnswered = responses.some(r => r.participantId === player.participantId && r.questionId === question.id);
      
      if (alreadyAnswered) {
        playState = "submitted";
      } else {
        playState = "answering";
        questionStartTime = Date.parse(session.currentQuestionStartedAt) || Date.now();
      }
    } else if (session.status === "results_revealed") {
      playState = "question_ended";
    } else if (session.status === "finished") {
      playState = "ended";
    }

    if (playState === "waiting_in_lobby") {
      renderLobbyWaiting();
    } else if (playState === "answering") {
      renderAnswering();
    } else if (playState === "submitted") {
      renderSubmitted();
    } else if (playState === "question_ended") {
      await renderQuestionEnded();
    } else if (playState === "ended") {
      renderEnded();
    }
  };

  const renderLobbyWaiting = () => {
    container.innerHTML = `
      <div style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px 24px; align-items:center; justify-content:center; text-align:center; min-height:90vh;">
        <div class="pulse-heart" style="color:var(--color-primary); margin-bottom:24px;">${icon('heart', 'reaction-float-icon')}</div>
        ${game.brand?.name ? `<div class="session-brand" style="margin-bottom:20px;"><strong>${escapeHTML(game.brand.name)}</strong>${game.brand.tagline ? `<span>${escapeHTML(game.brand.tagline)}</span>` : ''}</div>` : ''}
        <h1 style="font-size:24px; font-weight:800; margin-bottom:8px;">${i18n.t('joined_lobby')}</h1>
        <p style="color:var(--color-text-secondary); font-size:14px; margin-bottom:32px;">
          ${i18n.t('nickname')}: <strong style="color:var(--color-text-primary);">${escapeHTML(player.nickname)}</strong> | PIN: <strong>${escapeHTML(player.pin)}</strong>
        </p>
        <span style="font-size:12px; color:var(--color-text-muted); font-weight:600; text-transform:uppercase; letter-spacing:1px;">
          ${i18n.t('play_waiting_question')}
        </span>
      </div>
    `;
  };

  const renderAnswering = () => {
    const question = game.questions[session.currentQuestionIndex];
    const colors = ['#ff3b5c', '#0070f3', '#f5a623', '#00df89'];

    container.innerHTML = `
      <div style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); justify-content:space-between; min-height:95vh;">
        <!-- Top Status -->
        <div style="padding:16px 20px; display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid var(--color-border);">
          <span style="font-weight:700; font-size:14px;">${i18n.t('question')} ${session.currentQuestionIndex + 1}</span>
          ${game.brand?.name ? `<span class="player-brand-name">${escapeHTML(game.brand.name)}</span>` : ''}
          <span class="badge badge-red" id="playerTimerCountdown" style="font-size:14px; padding:4px 12px; font-weight:800;">${question.timeLimit}s</span>
        </div>

        <!-- Answers Buttons Grid -->
        <div class="play-grid">
          ${question.options.map((opt, idx) => `
            <button class="play-btn btn-opt-${opt.id}" data-opt-id="${opt.id}">
              <span class="answer-label" aria-hidden="true">${String.fromCharCode(65 + idx)}</span>
              <span class="play-btn-text">${opt.id}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;

    // Local Timer sync to protect client display
    let timerVal = Math.max(0, Math.ceil((question.timeLimit * 1000 - (Date.now() - questionStartTime)) / 1000));
    const interval = setInterval(() => {
      timerVal--;
      const countdownEl = container.querySelector('#playerTimerCountdown');
      if (countdownEl) countdownEl.innerText = `${timerVal}s`;
      if (timerVal <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    // Bind click options
    container.querySelectorAll('.play-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        clearInterval(interval);
        const selected = e.currentTarget.dataset.optId;
        await submitPlayerAnswer(selected);
      });
    });
  };

  const renderSubmitted = () => {
    container.innerHTML = `
      <div style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px 24px; align-items:center; justify-content:center; text-align:center; min-height:90vh;">
        <div class="pulse-heart" style="color:var(--color-primary); margin-bottom:20px;">${icon('clock', 'reaction-float-icon')}</div>
        <h2 style="font-size:22px; font-weight:800; margin-bottom:8px;">${i18n.t('answer_recorded')}</h2>
        <p style="color:var(--color-text-secondary); font-size:13px; margin-bottom:40px;">
          ${i18n.t('waiting_others')}
        </p>

        <!-- Dynamic Reaction panel -->
        <div class="card" style="width:100%; max-width:320px; background: var(--color-surface); padding:20px;">
          <h3 style="font-size:13px; font-weight:700; color:var(--color-text-secondary); margin-bottom:12px; text-transform:uppercase;">
            ${i18n.t('send_reaction')}
          </h3>
          <div style="display:flex; justify-content:space-around;">
            ${reactionButtons()}
          </div>
        </div>
      </div>
    `;

    bindReactions();
  };

  const renderQuestionEnded = async () => {
    // If we don't have results in state, query from database
    if (!submissionResult) {
      const q = game.questions[session.currentQuestionIndex];
      const responses = await dbSim.getResponses(session.id);
      const resp = responses.find(r => r.participantId === player.participantId && r.questionId === q.id);
      const allParts = await dbSim.getParticipants(session.id);
      const part = allParts.find(p => p.id === player.participantId);
      
      if (resp) {
        submissionResult = {
          isCorrect: resp.isCorrect,
          pointsAwarded: resp.pointsAwarded,
          score: part.score,
          streak: part.streak
        };
      }
    }

    const isCorrect = submissionResult?.isCorrect;
    const points = submissionResult?.pointsAwarded || 0;
    const streak = submissionResult?.streak || 0;
    const score = submissionResult?.score || 0;

    container.innerHTML = `
      <div style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px 24px; align-items:center; justify-content:space-between; text-align:center; min-height:90vh; transition: background 0.3s ease;">
        
        <div></div>

        <!-- Center message -->
        <div>
          <div style="margin-bottom:16px; color:${isCorrect ? 'var(--color-success)' : 'var(--color-danger)'};">
            ${isCorrect ? icon('fire', 'result-icon') : icon('xCircle', 'result-icon')}
          </div>
          <h1 style="font-size:36px; font-weight:800; color:var(--color-text-primary);">
            ${isCorrect ? i18n.t('play_correct') : i18n.t('play_incorrect')}
          </h1>
          
          ${isCorrect ? `
            <div style="font-size:24px; font-weight:800; color:var(--color-success); margin-top:12px;">
              +${points} ${i18n.t('play_points')}
            </div>
            ${streak >= 2 ? `
              <div class="badge badge-red" style="margin-top:12px; font-size:13px; padding:6px 14px; background:#fff; color:var(--color-primary); box-shadow:var(--shadow-premium);">
                ${icon('fire')} ${streak} ${i18n.t('correct_streak')}
              </div>
            ` : ''}
          ` : `
            <p style="color:var(--color-text-secondary); font-size:15px; margin-top:8px;">
              ${i18n.t('keep_trying')}
            </p>
          `}
        </div>

        <!-- Footer Stats -->
        <div style="width:100%; display:flex; flex-direction:column; gap:20px; align-items:center;">
          <div style="display:flex; justify-content:space-around; width:100%; max-width:320px; background:var(--color-surface-subtle); border:1px solid var(--color-border); padding:16px; border-radius:var(--radius-lg); color:var(--color-text-primary); font-weight:700;">
            <div>
              <div style="font-size:11px; opacity:0.7;">${i18n.t('total_score')}</div>
              <div style="font-size:20px; font-weight:800;">${score}</div>
            </div>
            <div style="width:1px; background:rgba(255,255,255,0.1);"></div>
            <div>
              <div style="font-size:11px; opacity:0.7;">${i18n.t('status')}</div>
              <div style="font-size:14px; margin-top:4px;">${i18n.t('see_host_screen')}</div>
            </div>
          </div>

          <!-- Reactions Panel while waiting -->
          <div class="card" style="width:100%; max-width:320px; padding:16px;">
            <div style="display:flex; justify-content:space-around;">
              ${reactionButtons()}
            </div>
          </div>
        </div>

      </div>
    `;

    bindReactions();
  };

  const renderEnded = () => {
    container.innerHTML = `
      <div style="flex:1; display:flex; flex-direction:column; background: var(--color-bg); padding:40px 24px; align-items:center; justify-content:center; text-align:center; min-height:90vh;">
        <div style="color:#fbbf24; margin-bottom:24px;">${icon('trophy', 'reaction-float-icon')}</div>
        <h1 style="font-size:28px; font-weight:800; margin-bottom:8px;">${i18n.t('game_finished')}</h1>
        <p style="color:var(--color-text-secondary); font-size:15px; margin-bottom:32px; max-width:280px;">
          ${i18n.t('podium_hint')}
        </p>
        <div class="card" style="width:100%; max-width:320px; padding:16px; margin-bottom:20px;">
          <div style="display:flex; justify-content:space-around;">${reactionButtons()}</div>
        </div>
        <button id="exitPlayBtn" class="btn btn-secondary" style="min-width:140px;">${i18n.t('exit_game')}</button>
      </div>
    `;

    container.querySelector('#exitPlayBtn').addEventListener('click', () => {
      playerSession.clear();
      window.location.hash = '#/';
    });
    bindReactions();
  };

  const submitPlayerAnswer = async (optionId) => {
    if (answerSubmitting) return;
    answerSubmitting = true;
    container.querySelectorAll('.play-btn').forEach(button => { button.disabled = true; });
    const elapsedMs = Date.now() - questionStartTime;
    const question = game.questions[session.currentQuestionIndex];
    try {
      // D1's unique participant/question key makes this operation idempotent.
      const res = await dbSim.submitResponse(player.participantId, question.id, optionId, elapsedMs);
      submissionResult = res;
      realtimeSim.publish("SUBMIT_ANSWER", {
        participantId: player.participantId,
        nickname: player.nickname,
        questionId: question.id,
        isCorrect: res.isCorrect
      });
      playState = "submitted";
      await render();
    } catch (error) {
      // The response may have reached D1 even if the mobile connection lost the reply.
      // Reconciliation will detect it and move the player to the submitted screen.
      await syncFromServer(true);
      window.showToast(error.message || i18n.t('network_error'));
    } finally {
      answerSubmitting = false;
      container.querySelectorAll('.play-btn').forEach(button => { button.disabled = false; });
    }
  };

  const bindReactions = () => {
    container.querySelectorAll('.reaction-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const reaction = e.currentTarget.dataset.reaction;
        
        // Broadcast Reaction
        realtimeSim.publish("SEND_REACTION", {
          reaction,
          nickname: player.nickname
        });
      });
    });
  };

  // Connect to realtime channel
  let syncing = false;
  const syncFromServer = async (force = false) => {
    if (syncing || !navigator.onLine) return;
    syncing = true;
    try {
      const latest = await dbSim.getSession(player.sessionId);
      if (!latest) throw new Error('Session unavailable');
      const changed = force || latest.status !== session.status || latest.currentQuestionIndex !== session.currentQuestionIndex || latest.currentQuestionStartedAt !== session.currentQuestionStartedAt;
      session = latest;
      if (changed) {
        game = await dbSim.getSessionGame(session.id);
        questionStartTime = null;
        submissionResult = null;
        await render();
      }
    } catch (error) {
      console.warn('[Play] State sync failed; will retry:', error.message);
    } finally {
      syncing = false;
    }
  };

  realtimeSim.connect(player.pin, async (type, payload) => {
    if (type === 'CONNECTION_RESTORED') {
      await syncFromServer(true);
    } else
    if (type === "START_GAME") {
      questionStartTime = null;
      submissionResult = null;
      playState = "answering";
      await render();
    } else if (type === "NEXT_QUESTION") {
      questionStartTime = null;
      submissionResult = null;
      playState = "answering";
      await render();
    } else if (type === "SHOW_RESULTS") {
      playState = "question_ended";
      await render();
    } else if (type === "GAME_OVER") {
      playState = "ended";
      await render();
    }
  });

  render();

  const statePoll = setInterval(() => syncFromServer(false), 3000);
  const resumeSync = () => { if (!document.hidden) syncFromServer(true); };
  window.addEventListener('online', resumeSync);
  document.addEventListener('visibilitychange', resumeSync);

  // Cleanup listeners on hash change
  window.addEventListener('hashchange', function cleanup() {
    clearInterval(statePoll);
    window.removeEventListener('online', resumeSync);
    document.removeEventListener('visibilitychange', resumeSync);
    realtimeSim.disconnect();
    window.removeEventListener('hashchange', cleanup);
  });
};
